import React, { useState, useMemo } from 'react';
import AppLayout from 'layouts/AppLayout';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import Icon from 'components/AppIcon';
import { NOTIFICATIONS_SEED } from './components/notificationsData';
import NotificationCard from './components/NotificationCard';
import FilterBar from './components/FilterBar';
import SettingsModal from './components/SettingsModal';

function Toast({ message, onClose }) {
  return (
    <div
      className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border border-border bg-card z-50 max-w-sm"
      style={{ zIndex: 9999 }}
    >
      <Icon name="CheckCircle" size={18} color="var(--color-success, #22c55e)" />
      <span className="text-sm text-foreground">{message}</span>
      <button onClick={onClose} className="ml-auto">
        <Icon name="X" size={14} color="var(--color-muted-foreground)" />
      </button>
    </div>
  );
}

function groupByDate(notifications) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today - 86400000);
  const weekAgo = new Date(today - 7 * 86400000);

  const groups = { Today: [], Yesterday: [], 'This Week': [], Earlier: [] };

  notifications?.forEach((n) => {
    const d = new Date(n?.timestamp);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day >= today) groups?.Today?.push(n);
    else if (day >= yesterday) groups?.Yesterday?.push(n);
    else if (day >= weekAgo) groups?.['This Week']?.push(n);
    else groups?.Earlier?.push(n);
  });

  return groups;
}

export default function NotificationsPage() {
  const { role, activeContext } = useRole();
  const isAdmin = role === ROLES.PLATFORM_ADMIN;
  const [notifications, setNotifications] = useState(NOTIFICATIONS_SEED);

  // Scope notifications to the current role and acting context
  const scopedNotifications = useMemo(() =>
    notifications?.filter((item) => {
      if (item?.roleTarget && !item?.roleTarget?.includes(role)) return false;
      if (item?.contextIds && !item?.contextIds?.includes(activeContext?.id)) return false;
      return true;
    }),
    [notifications, role, activeContext]
  );
  const [activeCategory, setActiveCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [dateRange, setDateRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  // Computed counts for category tabs
  const categoryCounts = useMemo(() => {
    const active = scopedNotifications?.filter((n) => !n?.isArchived);
    const archived = scopedNotifications?.filter((n) => n?.isArchived);
    const counts = { all: active?.length, archived: archived?.length };
    ['capital-call', 'milestone', 'esg', 'document', 'deal-movement']?.forEach((cat) => {
      counts[cat] = active?.filter((n) => n?.category === cat)?.length;
    });
    return counts;
  }, [scopedNotifications]);

  const unreadCount = useMemo(
    () => scopedNotifications?.filter((n) => !n?.isRead && !n?.isArchived)?.length,
    [scopedNotifications]
  );

  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    const isArchiveView = activeCategory === 'archived';
    let list = scopedNotifications?.filter((n) => (isArchiveView ? n?.isArchived : !n?.isArchived));

    if (!isArchiveView && activeCategory !== 'all') {
      list = list?.filter((n) => n?.category === activeCategory);
    }

    if (statusFilter === 'Unread') list = list?.filter((n) => !n?.isRead);
    if (statusFilter === 'Read') list = list?.filter((n) => n?.isRead);

    if (priorityFilter !== 'All') list = list?.filter((n) => n?.priority === priorityFilter);

    if (dateRange !== 'all') {
      const days = parseInt(dateRange);
      const cutoff = new Date(Date.now() - days * 86400000);
      list = list?.filter((n) => new Date(n?.timestamp) >= cutoff);
    }

    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(
        (n) =>
          n?.title?.toLowerCase()?.includes(q) ||
          n?.description?.toLowerCase()?.includes(q) ||
          n?.asset?.toLowerCase()?.includes(q)
      );
    }

    return list;
  }, [scopedNotifications, activeCategory, statusFilter, priorityFilter, dateRange, searchQuery]);

  const isArchiveView = activeCategory === 'archived';

  // Actions
  const handleMarkRead = (id) => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, isRead: !n?.isRead } : n))
    );
  };

  const handleArchive = (id) => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, isArchived: true } : n))
    );
    showToast('Notification archived.');
  };

  const handleRestore = (id) => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, isArchived: false } : n))
    );
    showToast('Notification restored.');
  };

  const handleDelete = (id) => {
    setNotifications((prev) => prev?.filter((n) => n?.id !== id));
    showToast('Notification permanently deleted.');
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev?.map((n) => (!n?.isArchived ? { ...n, isRead: true } : n)));
    showToast('All notifications marked as read.');
  };

  const handleArchiveAllRead = () => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.isRead && !n?.isArchived ? { ...n, isArchived: true } : n))
    );
    showToast('All read notifications archived.');
  };

  const handleClearArchived = () => {
    if (!isAdmin) return;
    setNotifications((prev) => prev?.filter((n) => !n?.isArchived));
    showToast('All archived notifications cleared.');
  };

  const handleClearFilters = () => {
    setActiveCategory('all');
    setStatusFilter('All');
    setPriorityFilter('All');
    setDateRange('all');
    setSearchQuery('');
  };

  const grouped = useMemo(() => groupByDate(filteredNotifications), [filteredNotifications]);
  const hasResults = filteredNotifications?.length > 0;
  const allRead = unreadCount === 0;

  return (
    <AppLayout>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          onSave={() => {
            setShowSettings(false);
            showToast('Notification preferences saved.');
          }}
        />
      )}

      <div className="min-h-screen bg-background">
        {/* Sticky page header */}
        <header
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground">Notifications</h1>
              {unreadCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  {unreadCount} unread
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleMarkAllRead}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="CheckCheck" size={15} />
                Mark all as read
              </button>
              <button
                onClick={handleArchiveAllRead}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Archive" size={15} />
                Archive all read
              </button>
              <button
                onClick={() => setShowSettings(true)}
                title="Notification settings"
                className="w-9 h-9 flex items-center justify-center bg-card border border-border rounded-lg hover:bg-muted text-muted-foreground transition-colors"
              >
                <Icon name="Settings" size={16} />
              </button>
            </div>
          </div>
          <p className="font-caption text-sm text-muted-foreground mt-1">
            Activity across your portfolio, deals, and documents —{' '}
            <span className="italic">Sample data, for demonstration purposes only</span>
          </p>
        </header>

        {/* Page content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">
          {/* Filter Bar */}
          <FilterBar
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            priorityFilter={priorityFilter}
            onPriorityChange={setPriorityFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            categoryCounts={categoryCounts}
          />

          {/* Archive view header */}
          {isArchiveView && filteredNotifications?.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="font-caption text-sm text-muted-foreground">{filteredNotifications?.length} archived notification{filteredNotifications?.length !== 1 ? 's' : ''}</p>
              {isAdmin && (
                <button
                  onClick={handleClearArchived}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Icon name="Trash2" size={13} />
                  Clear all archived
                </button>
              )}
            </div>
          )}

          {/* All caught up state */}
          {!isArchiveView && allRead && hasResults && (
            <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
              <Icon name="CheckCircle2" size={18} color="#16a34a" />
              <span className="font-caption text-sm font-medium text-green-700">You're all caught up — no unread notifications.</span>
            </div>
          )}

          {/* Empty state */}
          {!hasResults && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="BellOff" size={28} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground mb-1">No notifications in this category</h3>
              <p className="font-caption text-sm text-muted-foreground mb-4">Try adjusting your filters or check another category.</p>
              <button
                onClick={handleClearFilters}
                className="font-caption text-sm font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Notification feed grouped by date */}
          {hasResults && (
            <div className="space-y-8">
              {Object.entries(grouped)?.map(([group, items]) => {
                if (!items?.length) return null;
                return (
                  <div key={group}>
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-widest">{group}</h2>
                      <div className="flex-1 h-px bg-border" />
                      <span className="font-caption text-xs text-muted-foreground">{items?.length} item{items?.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-3">
                      {items?.map((notification) => (
                        <NotificationCard
                          key={notification?.id}
                          notification={notification}
                          onMarkRead={handleMarkRead}
                          onArchive={handleArchive}
                          onRestore={handleRestore}
                          onDelete={handleDelete}
                          isArchiveView={isArchiveView}
                          userRole={role}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
